/**
 * TotalPointsChallenges Component
 * Displays the linked challenges for total points challenges
 */

import React, { memo, useMemo } from 'react'
import { List } from '@material-ui/core'
import { CustomType, GoalWithProgress } from '../../../services/api.types'
import { TotalPointsLogic } from '../../ChallengeAdmin/components/ChallengeTotalPoints/ChallengeTotalPoints'
import { CustomTypeCategory } from '../../../services/CustomTypes'
import { YumActivityIcon } from '../../../components/YumActivityIcon/YumActivityIcon'
import ChallengeInfoListItem from './ChallengeInfoListItem'
import { getPeriodWithDays } from '../ChallengeInfo.helpers'
import { ChallengeInfoListItemData } from '../ChallengeInfo.types'

interface TotalPointsChallengesProps {
  totalPointsLogic: TotalPointsLogic[] | null
  goals: GoalWithProgress[]
  isReview?: boolean
  style?: React.CSSProperties
}

/**
 * TotalPointsChallenges shows the linked challenges that contribute to a total points challenge
 * Each linked challenge is displayed with its period and title
 */
const TotalPointsChallenges: React.FC<TotalPointsChallengesProps> = memo(
  ({ totalPointsLogic, goals, isReview = false }) => {
    // Early return if no total points logic
    if (!totalPointsLogic || totalPointsLogic.length === 0) {
      return null
    }

    // Memoize the processed points data
    const pointsData = useMemo(() => {
      const processedData: ChallengeInfoListItemData[] = []

      totalPointsLogic.forEach((row) => {
        // Find the corresponding challenge
        const challenge = goals?.find((g) => g.id === row.id)

        // Skip if challenge not found or not a points challenge
        if (!challenge || challenge.customType !== CustomType.POINTS) {
          return
        }

        processedData.push({
          label: getPeriodWithDays(challenge),
          content: challenge.title,
          Icon: (
            <YumActivityIcon
              category={CustomTypeCategory.POINTS}
              color={'darkBlue'}
              isInactive={true}
              extraPadding={true}
            />
          ),
        })
      })

      return processedData
    }, [totalPointsLogic, goals])

    // Don't render if no valid data
    if (pointsData.length === 0) {
      return null
    }

    const listStyle = { marginTop: isReview ? 0 : 10 }

    return (
      <List component="div" role="presentation" style={listStyle}>
        {pointsData.map((data, index) => (
          <ChallengeInfoListItem key={index} data={data} index={index} isDense={true} />
        ))}
      </List>
    )
  },
)

TotalPointsChallenges.displayName = 'TotalPointsChallenges'

export default TotalPointsChallenges
